// src/cockpit4/components/FinancialForecast.tsx
// ============================================
// 财务预测（财务中心 P1-功能5）
//
// 功能：
//   1. 收入/支出/利润/现金流 4 个指标预测
//   2. 乐观(+20%) / 中性 / 悲观(-20%) 三情景对比 + 全部对比模式
//   3. 未来 7 天 / 30 天 / 90 天 时间范围切换
//   4. recharts 折线图：历史实际线 + 三条情景预测线 + 置信区间阴影
//   5. 预测摘要卡片：预计总收入/总支出/净利润/期末现金流
//   6. 预测准确度追踪：记录历史预测值，与实际值对比，计算误差率
//
// 数据来源（白皮书铁律一：禁止假数据）：
//   1. RuntimeManager.query('revenue_engine', { type: 'today_revenue' })
//      → RevenueEngine 聚合的真实今日收入（todayAmount），作为最新真实数据点
//   2. localStorage(key: xeaos_finance_dashboard_targets).snapshots
//      → 每日真实收入快照（由 FinanceDashboard 从 RevenueEngine 自动记录），构成预测的历史时间序列
//   3. localStorage(key: xeaos_finance_statements)
//      → 历史财务报表（本月+上月），用于派生支出/利润/现金流的真实占比与趋势
//   支出/利润/现金流 日序列 = 真实每日收入 × 真实报表占比（绝不凭空捏造）
//   无足够历史数据（<2 个真实收入点）时显示"暂无历史数据 · 无法预测"
//
// 预测算法（内置，不依赖外部 AI）：
//   - movingAverage(data, window)        移动平均
//   - linearRegression(points)           线性回归（最小二乘法）
//   - seasonalIndex(data)                季节性指数（按 7 天周期归一化）
//   - forecast(historical, days, scenario) 综合预测 = (移动平均 + 趋势增量) × 季节性 × 情景系数
//   - confidenceBand(forecast, historical, window) 基于历史残差标准差的置信区间
//
// 权限：财务中心仅超级管理员开放（useUserStore.isSuperAdmin），非超管显示权限提示。
// ============================================

import { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ComposedChart,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  Wallet,
  TrendingUp,
  TrendingDown,
  Activity,
  Lock,
  RefreshCw,
  AlertCircle,
  Database,
  Loader2,
  Calculator,
  Target,
  BarChart3,
  Clock,
  Sparkles,
  CheckCircle2,
  ArrowUpRight,
  ArrowDownRight,
} from "lucide-react";
import RuntimeManager from "../../runtime/RuntimeManager";
import { useUserStore } from "../../stores/userStore";

// ============================================
// 类型定义
// ============================================

/** 预测情景 */
type ForecastScenario = "optimistic" | "neutral" | "pessimistic";

/** 预测指标 */
type ForecastMetric = "revenue" | "expense" | "profit" | "cashflow";

/** 预测时间范围（天） */
type ForecastRange = 7 | 30 | 90;

/** 图表数据点 */
interface ChartPoint {
  date: string;
  actual: number | null;           // 历史实际值
  neutral?: number | null;          // 中性预测
  optimistic?: number | null;       // 乐观预测
  pessimistic?: number | null;      // 悲观预测
  range?: [number, number] | null;  // 置信区间 [下限, 上限]
}

/** 预测记录（用于准确度追踪） */
interface ForecastRecord {
  id: string;
  metric: ForecastMetric;
  scenario: ForecastScenario;
  forecastDate: string;   // 预测生成日期
  targetDate: string;     // 预测目标日期
  forecastValue: number;
  actualValue?: number;   // 实际发生后回填
  accuracy?: number;      // 准确度（1 - |实际-预测|/实际）
}

/** 历史时间序列点 */
interface SeriesPoint {
  date: string;
  value: number;
}

/** 历史财务报表数据（与 FinancialStatements 的 localStorage 结构对齐，仅取所需字段） */
interface StatementData {
  revenue: number;
  costOfGoodsSold: number;
  operatingExpenses: number;
  marketingExpenses: number;
  adminExpenses: number;
  taxExpenses: number;
  operatingCashFlow: number;
}

/** localStorage(xeaos_finance_statements) 持久化结构 */
interface PersistedFinance {
  current: StatementData;
  previousMonth: StatementData | null;
}

// ============================================
// 常量
// ============================================

const STATEMENTS_KEY = "xeaos_finance_statements";
const DASHBOARD_KEY = "xeaos_finance_dashboard_targets";
const RECORDS_KEY = "xeaos_finance_forecast_records";

/** 情景系数与配色 */
const SCENARIO_CONFIG: Record<
  ForecastScenario,
  { label: string; factor: number; color: string; desc: string }
> = {
  optimistic: { label: "乐观", factor: 1.2, color: "#10B981", desc: "+20%" },
  neutral: { label: "中性", factor: 1.0, color: "#06B6D4", desc: "基准" },
  pessimistic: { label: "悲观", factor: 0.8, color: "#EF4444", desc: "-20%" },
};

/** 指标配置 */
const METRIC_CONFIG: Record<
  ForecastMetric,
  { label: string; en: string; icon: typeof TrendingUp; color: string }
> = {
  revenue: { label: "收入", en: "Revenue", icon: TrendingUp, color: "#10B981" },
  expense: { label: "支出", en: "Expense", icon: TrendingDown, color: "#EF4444" },
  profit: { label: "利润", en: "Profit", icon: Activity, color: "#06B6D4" },
  cashflow: { label: "现金流", en: "Cash Flow", icon: Wallet, color: "#A855F7" },
};

const RANGE_OPTIONS: ForecastRange[] = [7, 30, 90];

// ============================================
// 预测算法（内置，不依赖外部 AI）
// ============================================

/** 移动平均：取最近 window 个数据点的算术平均 */
function movingAverage(data: number[], window: number): number {
  if (data.length === 0) return 0;
  const w = Math.max(1, Math.min(window, data.length));
  let sum = 0;
  for (let i = data.length - w; i < data.length; i++) sum += data[i];
  return sum / w;
}

/** 线性回归（最小二乘法）：返回斜率与截距 */
function linearRegression(
  points: Array<{ x: number; y: number }>
): { slope: number; intercept: number } {
  const n = points.length;
  if (n === 0) return { slope: 0, intercept: 0 };
  if (n === 1) return { slope: 0, intercept: points[0].y };
  let sumX = 0,
    sumY = 0,
    sumXY = 0,
    sumXX = 0;
  for (const p of points) {
    sumX += p.x;
    sumY += p.y;
    sumXY += p.x * p.y;
    sumXX += p.x * p.x;
  }
  const denom = n * sumXX - sumX * sumX;
  if (Math.abs(denom) < 1e-9) return { slope: 0, intercept: sumY / n };
  const slope = (n * sumXY - sumX * sumY) / denom;
  const intercept = (sumY - slope * sumX) / n;
  return { slope, intercept };
}

/** 季节性指数（按 7 天周期计算，返回每个周期位置的归一化指数） */
function seasonalIndex(data: number[]): number[] {
  const n = data.length;
  if (n === 0) return [];
  const sums = new Array(7).fill(0);
  const counts = new Array(7).fill(0);
  for (let i = 0; i < n; i++) {
    const pos = i % 7;
    sums[pos] += data[i];
    counts[pos]++;
  }
  const total = data.reduce((s, v) => s + v, 0);
  const avg = total / n;
  const indices = new Array<number>(7);
  for (let i = 0; i < 7; i++) {
    indices[i] = counts[i] > 0 && avg > 0 ? sums[i] / counts[i] / avg : 1;
  }
  return indices;
}

/**
 * 综合预测：趋势(移动平均 + 线性回归增量) × 季节性 × 情景系数
 * @param historical 历史日序列（由早到晚）
 * @param days 预测天数
 * @param scenario 情景
 */
function forecast(
  historical: number[],
  days: number,
  scenario: ForecastScenario
): number[] {
  if (historical.length === 0) return new Array(days).fill(0);
  const factor = SCENARIO_CONFIG[scenario].factor;
  const maWindow = Math.min(7, historical.length);
  const base = movingAverage(historical, maWindow);
  const { slope } = linearRegression(historical.map((y, x) => ({ x, y })));
  const seasons = seasonalIndex(historical);
  const n = historical.length;
  const result: number[] = [];
  for (let d = 1; d <= days; d++) {
    const trendDelta = slope * d;
    const season = seasons.length > 0 ? seasons[(n + d - 1) % 7] : 1;
    let val = (base + trendDelta) * season * factor;
    if (!Number.isFinite(val) || val < 0) val = 0;
    result.push(val);
  }
  return result;
}

/** 置信区间：基于历史残差标准差，随预测步长扩大（±1.5σ × √步长） */
function confidenceBand(
  forecastValues: number[],
  historical: number[],
  maWindow: number
): { lower: number[]; upper: number[] } {
  const w = Math.max(2, Math.min(maWindow, historical.length));
  const residuals: number[] = [];
  for (let i = w; i < historical.length; i++) {
    const ma = historical.slice(i - w, i).reduce((s, v) => s + v, 0) / w;
    residuals.push(historical[i] - ma);
  }
  const mean =
    residuals.length > 0
      ? residuals.reduce((s, v) => s + v, 0) / residuals.length
      : 0;
  const variance =
    residuals.length > 0
      ? residuals.reduce((s, v) => s + (v - mean) * (v - mean), 0) /
        residuals.length
      : 0;
  const sigma = Math.sqrt(variance);
  const lower = forecastValues.map((v, i) =>
    Math.max(0, v - 1.5 * sigma * Math.sqrt(i + 1))
  );
  const upper = forecastValues.map((v, i) =>
    v + 1.5 * sigma * Math.sqrt(i + 1)
  );
  return { lower, upper };
}

// ============================================
// 工具函数
// ============================================

/** 金额格式化（元 → 万元/元） */
function formatMoney(amount: number): string {
  const num = Number(amount);
  if (!Number.isFinite(num)) return "¥ 0";
  if (Math.abs(num) >= 10000) return `¥ ${(num / 10000).toFixed(2)} 万`;
  return `¥ ${num.toLocaleString("zh-CN", { maximumFractionDigits: 2 })}`;
}

/** 百分比格式化（0-1 → x%） */
function formatPercent(ratio: number): string {
  const num = Number(ratio);
  if (!Number.isFinite(num)) return "0%";
  return `${(num * 100).toFixed(1)}%`;
}

/** YYYY-MM-DD → MM-DD（图表 X 轴用） */
function fmtDate(iso: string): string {
  return iso && iso.length >= 10 ? iso.slice(5, 10) : iso;
}

/** 今日日期 YYYY-MM-DD */
function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

/** 读取历史财务报表（xeaos_finance_statements） */
function loadStatements(): PersistedFinance | null {
  try {
    const raw = localStorage.getItem(STATEMENTS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.current) return null;
    return parsed as PersistedFinance;
  } catch {
    return null;
  }
}

/** 读取每日真实收入快照（xeaos_finance_dashboard_targets.snapshots） */
function loadSnapshots(): SeriesPoint[] {
  try {
    const raw = localStorage.getItem(DASHBOARD_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed?.snapshots)) return [];
    const list: SeriesPoint[] = [];
    for (const s of parsed.snapshots) {
      if (s && typeof s.date === "string" && typeof s.amount === "number" && s.amount > 0) {
        list.push({ date: s.date, value: s.amount });
      }
    }
    return list.sort((a, b) => a.date.localeCompare(b.date));
  } catch {
    return [];
  }
}

/** 记录今日真实收入快照（与 FinanceDashboard 行为一致，自动积累历史，禁止假数据） */
function recordTodaySnapshot(amount: number): void {
  if (!(amount > 0)) return;
  try {
    const raw = localStorage.getItem(DASHBOARD_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    const snapshots: Array<{ date: string; amount: number }> = Array.isArray(parsed?.snapshots)
      ? parsed.snapshots
      : [];
    const today = todayISO();
    if (snapshots.some((s) => s.date === today)) return;
    snapshots.push({ date: today, amount });
    parsed.snapshots = snapshots;
    localStorage.setItem(DASHBOARD_KEY, JSON.stringify(parsed));
  } catch {
    // 写入失败不阻塞
  }
}

/** 读取预测记录 */
function loadRecords(): ForecastRecord[] {
  try {
    const raw = localStorage.getItem(RECORDS_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return [];
    return arr.filter(
      (r: any) => r && typeof r.targetDate === "string" && typeof r.metric === "string"
    );
  } catch {
    return [];
  }
}

/** 写入预测记录 */
function saveRecords(records: ForecastRecord[]): void {
  try {
    localStorage.setItem(RECORDS_KEY, JSON.stringify(records));
  } catch {
    // ignore
  }
}

/**
 * 构建 4 个指标的历史日序列（全部基于真实数据派生）
 * - revenue：真实每日收入快照
 * - expense：每日收入 × 真实支出/收入占比（来自报表）
 * - profit：每日收入 - 每日支出
 * - cashflow：每日收入 × 真实经营现金流/收入占比（来自报表）
 */
function buildAllSeries(
  revenueSeries: SeriesPoint[],
  statements: PersistedFinance | null
): Record<ForecastMetric, SeriesPoint[]> {
  const cur = statements?.current;
  const totalExpense = cur
    ? (cur.costOfGoodsSold || 0) +
      (cur.operatingExpenses || 0) +
      (cur.marketingExpenses || 0) +
      (cur.adminExpenses || 0) +
      (cur.taxExpenses || 0)
    : 0;
  const monthRevenue = cur?.revenue || 0;
  // 支出占收入比（dimensionless，可作用于每日收入）
  const expenseRatio = monthRevenue > 0 ? totalExpense / monthRevenue : 0;
  // 经营现金流占收入比
  const cashflowRatio =
    monthRevenue > 0 ? (cur?.operatingCashFlow || 0) / monthRevenue : 0;

  const expense = revenueSeries.map((p) => ({
    date: p.date,
    value: p.value * expenseRatio,
  }));
  const profit = revenueSeries.map((p, i) => ({
    date: p.date,
    value: p.value - expense[i].value,
  }));
  const cashflow = revenueSeries.map((p) => ({
    date: p.date,
    value: p.value * cashflowRatio,
  }));
  return { revenue: revenueSeries, expense, profit, cashflow };
}

/** 构建图表数据：历史实际线 + 桥接点 + 预测段（含 3 情景与置信区间） */
function buildChartData(
  hist: SeriesPoint[],
  fc: { optimistic: number[]; neutral: number[]; pessimistic: number[] },
  band: { lower: number[]; upper: number[] },
  range: number
): ChartPoint[] {
  if (hist.length === 0) return [];
  // 历史展示窗口：最多 60 天，保证可读性
  const histWindow = Math.min(hist.length, 60);
  const shown = hist.slice(-histWindow);
  const last = shown[shown.length - 1];
  const lastDate = new Date(last.date);
  // 生成未来 range 天的日期
  const futureDates: string[] = [];
  for (let i = 1; i <= range; i++) {
    const d = new Date(lastDate);
    d.setDate(d.getDate() + i);
    futureDates.push(d.toISOString().slice(0, 10));
  }

  const data: ChartPoint[] = [];
  // 历史段（除最后一个点，仅 actual）
  for (let i = 0; i < shown.length - 1; i++) {
    data.push({ date: fmtDate(shown[i].date), actual: shown[i].value });
  }
  // 桥接点：最后一个历史点，同时承载 actual 与 3 情景预测起点，保证折线连续
  data.push({
    date: fmtDate(last.date),
    actual: last.value,
    neutral: last.value,
    optimistic: last.value,
    pessimistic: last.value,
    range: [last.value, last.value],
  });
  // 预测段
  for (let i = 0; i < range; i++) {
    data.push({
      date: fmtDate(futureDates[i]),
      actual: null,
      neutral: fc.neutral[i],
      optimistic: fc.optimistic[i],
      pessimistic: fc.pessimistic[i],
      range: [band.lower[i], band.upper[i]],
    });
  }
  return data;
}

/**
 * 记录预测（中性，4 指标，90 天）
 * 仅记录尚未存在的未来目标日期，保留最早预测用于准确度比对
 */
function recordForecasts(
  allSeries: Record<ForecastMetric, SeriesPoint[]>,
  existing: ForecastRecord[]
): ForecastRecord[] {
  const today = todayISO();
  const horizon = 90;
  const next: ForecastRecord[] = [...existing];
  const has = new Set(
    next.map((r) => `${r.metric}|${r.scenario}|${r.targetDate}`)
  );
  const forecastDate = new Date().toISOString();
  const metrics: ForecastMetric[] = ["revenue", "expense", "profit", "cashflow"];
  let added = false;

  for (const metric of metrics) {
    const series = allSeries[metric];
    if (series.length === 0) continue;
    const hist = series.map((p) => p.value);
    const fc = forecast(hist, horizon, "neutral");
    const lastDateStr = series[series.length - 1]?.date;
    if (!lastDateStr) continue;
    const base = new Date(lastDateStr);
    for (let i = 0; i < horizon; i++) {
      const d = new Date(base);
      d.setDate(d.getDate() + i + 1);
      const targetDate = d.toISOString().slice(0, 10);
      if (targetDate <= today) continue; // 仅记录未来日期
      const key = `${metric}|neutral|${targetDate}`;
      if (has.has(key)) continue;
      next.push({
        id: `fc-${metric}-${targetDate}-${Date.now()}-${i}`,
        metric,
        scenario: "neutral",
        forecastDate,
        targetDate,
        forecastValue: fc[i],
      });
      has.add(key);
      added = true;
    }
  }
  if (!added) return existing; // 无新增，返回原引用避免循环
  // 剪枝：仅保留最近 180 天内目标日期
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - 180);
  const cutoffStr = cutoff.toISOString().slice(0, 10);
  return next.filter((r) => r.targetDate >= cutoffStr);
}

/** 回填实际值（仅 revenue 有独立真实实际值，来自每日收入快照） */
function backfillActuals(
  records: ForecastRecord[],
  revenueSeries: SeriesPoint[]
): ForecastRecord[] {
  const map = new Map<string, number>();
  for (const p of revenueSeries) map.set(p.date, p.value);
  const today = todayISO();
  let changed = false;
  const next = records.map((r) => {
    if (r.actualValue !== undefined) return r;
    if (r.metric !== "revenue") return r; // 仅 revenue 回填
    if (r.targetDate > today) return r;
    const actual = map.get(r.targetDate);
    if (actual === undefined || actual <= 0) return r;
    const accuracy =
      1 - Math.abs(actual - r.forecastValue) / Math.max(Math.abs(actual), 1e-9);
    changed = true;
    return { ...r, actualValue: actual, accuracy: Math.max(0, accuracy) };
  });
  return changed ? next : records;
}

// ============================================
// 子组件：摘要卡片
// ============================================

interface SummaryCardProps {
  label: string;
  value: string;
  icon: typeof TrendingUp;
  color: string;
  hint: string;
  delay?: number;
}

function SummaryCard({ label, value, icon: Icon, color, hint, delay = 0 }: SummaryCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay }}
      className="relative rounded-lg border border-white/[0.06] bg-white/[0.02] backdrop-blur-md p-3 overflow-hidden"
    >
      <div
        className="absolute top-0 left-0 right-0 h-px"
        style={{ background: `linear-gradient(90deg, transparent, ${color}80, transparent)` }}
      />
      <div className="flex items-center justify-between mb-1.5">
        <div
          className="w-6 h-6 rounded-md flex items-center justify-center"
          style={{ background: `${color}15`, border: `1px solid ${color}30` }}
        >
          <Icon size={12} style={{ color }} />
        </div>
        <span className="text-[8px] font-mono text-gray-600 uppercase tracking-wider">
          {hint}
        </span>
      </div>
      <div className="text-[15px] font-mono font-bold text-white tracking-tight leading-none">
        {value}
      </div>
      <div className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mt-1">
        {label}
      </div>
    </motion.div>
  );
}

// ============================================
// 主组件
// ============================================

export default function FinancialForecast() {
  // 权限：财务中心仅超级管理员开放
  const isSuperAdmin = useUserStore((s) => s.isSuperAdmin());

  // 控制项
  const [metric, setMetric] = useState<ForecastMetric>("revenue");
  const [scenario, setScenario] = useState<ForecastScenario | "all">("neutral");
  const [range, setRange] = useState<ForecastRange>(30);

  // 数据
  const [revenueSeries, setRevenueSeries] = useState<SeriesPoint[]>([]);
  const [statements, setStatements] = useState<PersistedFinance | null>(null);
  const [records, setRecords] = useState<ForecastRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [lastUpdated, setLastUpdated] = useState("");

  // ============================================
  // 加载真实数据（RevenueEngine + localStorage）
  // ============================================
  const loadForecastData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      // 轮询等待 RuntimeManager 初始化（30+ Runtime 全部初始化需要时间）
      let ready = false;
      for (let i = 0; i < 40; i++) {
        if (RuntimeManager.isInitialized()) {
          ready = true;
          break;
        }
        await new Promise((r) => setTimeout(r, 300));
      }

      let todayAmount = 0;
      if (ready && RuntimeManager.hasRuntime("revenue_engine" as any)) {
        const resp = await RuntimeManager.query("revenue_engine" as any, {
          type: "today_revenue",
        });
        // 双层解包：RevenueEngine 返回 {success,data,...}，又被 RuntimeManager 包裹为 {success, data: <上述>}
        const inner = resp?.success ? resp.data : null;
        const data =
          inner && typeof inner === "object" && "data" in inner
            ? (inner as { data?: unknown }).data
            : inner;
        todayAmount = Number((data as any)?.todayAmount) || 0;
        // 记录今日真实收入快照（积累历史，禁止假数据）
        recordTodaySnapshot(todayAmount);
      }

      // 读取历史每日收入快照（真实数据）+ 财务报表
      const snaps = loadSnapshots();
      const stmts = loadStatements();
      const today = todayISO();
      const seen = new Set<string>();
      const series: SeriesPoint[] = [];
      for (const s of snaps) {
        if (!seen.has(s.date)) {
          series.push(s);
          seen.add(s.date);
        }
      }
      // 追加今日真实收入（若快照中尚无今日）
      if (todayAmount > 0 && !seen.has(today)) {
        series.push({ date: today, value: todayAmount });
        seen.add(today);
      }
      series.sort((a, b) => a.date.localeCompare(b.date));

      setRevenueSeries(series);
      setStatements(stmts);
      setRecords(loadRecords());
      setLastUpdated(
        new Date().toLocaleString("zh-CN", { hour12: false })
      );
    } catch (err: any) {
      setError(err?.message || "加载预测数据失败");
      // 失败时仍尝试从 localStorage 读取，绝不展示假数据
      setRevenueSeries(loadSnapshots());
      setStatements(loadStatements());
      setRecords(loadRecords());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadForecastData();
  }, [loadForecastData]);

  // 是否有足够历史数据进行预测（至少 2 个真实收入点）
  const hasHistory = revenueSeries.length >= 2;

  // ============================================
  // 派生：4 指标历史日序列
  // ============================================
  const allSeries = useMemo(
    () => buildAllSeries(revenueSeries, statements),
    [revenueSeries, statements]
  );

  // 预测准确度追踪：回填实际值 + 记录未来预测（仅在已有历史时）
  useEffect(() => {
    if (!hasHistory) return;
    setRecords((prev) => {
      const withActuals = backfillActuals(prev, revenueSeries);
      const withForecasts = recordForecasts(allSeries, withActuals);
      if (withForecasts === prev) return prev;
      saveRecords(withForecasts);
      return withForecasts;
    });
  }, [allSeries, revenueSeries, hasHistory]);

  // ============================================
  // 派生：选中指标的历史值与 3 情景预测
  // ============================================
  const histValues = useMemo(
    () => allSeries[metric].map((p) => p.value),
    [allSeries, metric]
  );

  const forecasts = useMemo(
    () => ({
      optimistic: forecast(histValues, range, "optimistic"),
      neutral: forecast(histValues, range, "neutral"),
      pessimistic: forecast(histValues, range, "pessimistic"),
    }),
    [histValues, range]
  );

  // 置信区间基于"聚焦情景"：全部对比时用中性，单选时用所选情景
  const focusedScenario: ForecastScenario =
    scenario === "all" ? "neutral" : scenario;
  const band = useMemo(
    () => confidenceBand(forecasts[focusedScenario], histValues, 7),
    [forecasts, histValues, focusedScenario]
  );

  // 图表数据
  const chartData = useMemo(
    () => buildChartData(allSeries[metric], forecasts, band, range),
    [allSeries, metric, forecasts, band, range]
  );

  // ============================================
  // 派生：摘要卡片（始终基于 revenue/expense/cashflow 中性预测）
  // ============================================
  const summary = useMemo(() => {
    const revHist = allSeries.revenue.map((p) => p.value);
    const expHist = allSeries.expense.map((p) => p.value);
    const cfHist = allSeries.cashflow.map((p) => p.value);
    const revFc = forecast(revHist, range, "neutral");
    const expFc = forecast(expHist, range, "neutral");
    const cfFc = forecast(cfHist, range, "neutral");
    const sum = (arr: number[]) => arr.reduce((s, v) => s + v, 0);
    const totalRev = sum(revFc);
    const totalExp = sum(expFc);
    const baselineCF = statements?.current?.operatingCashFlow || 0;
    return {
      totalRevenue: totalRev,
      totalExpense: totalExp,
      netProfit: totalRev - totalExp,
      endingCashflow: baselineCF + sum(cfFc),
    };
  }, [allSeries, range, statements]);

  // ============================================
  // 派生：准确度统计
  // ============================================
  const accuracyStats = useMemo(() => {
    const withActual = records.filter(
      (r) => r.actualValue !== undefined && r.accuracy !== undefined
    );
    const recent = [...withActual]
      .sort((a, b) => b.targetDate.localeCompare(a.targetDate))
      .slice(0, 8);
    const avg =
      withActual.length > 0
        ? withActual.reduce((s, r) => s + (r.accuracy || 0), 0) / withActual.length
        : null;
    return { recent, avg, count: withActual.length };
  }, [records]);

  const metricCfg = METRIC_CONFIG[metric];

  // ============================================
  // 渲染：权限拦截
  // ============================================
  if (!isSuperAdmin) {
    return (
      <div className="h-full w-full overflow-hidden p-4">
        <div className="h-full flex flex-col items-center justify-center rounded-xl border border-red-500/20 bg-red-500/[0.02] backdrop-blur-xl">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="w-16 h-16 rounded-full bg-red-500/15 border border-red-500/30 flex items-center justify-center mb-4"
          >
            <Lock size={28} className="text-red-400" />
          </motion.div>
          <h2 className="text-[16px] font-semibold text-white mb-2">权限受限</h2>
          <p className="text-[12px] font-mono text-gray-400 text-center max-w-sm leading-relaxed">
            财务预测仅对超级管理员开放。
            <br />
            预测涉及企业核心财务数据，普通员工无权访问。
          </p>
          <p className="text-[10px] font-mono text-gray-600 mt-4">
            如需访问，请联系超级管理员授权。
          </p>
        </div>
      </div>
    );
  }

  // ============================================
  // 渲染：空状态（无足够历史数据）
  // ============================================
  if (!loading && !hasHistory) {
    return (
      <div className="h-full w-full overflow-hidden p-4">
        <div className="h-full flex flex-col rounded-xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-xl overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-3 border-b border-white/[0.06]">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-md bg-cyan-500/15 border border-cyan-500/30 flex items-center justify-center">
                <BarChart3 size={16} className="text-cyan-400" />
              </div>
              <div>
                <h2 className="text-[15px] font-semibold text-white tracking-tight">财务预测</h2>
                <p className="text-[10px] font-mono text-gray-500 uppercase tracking-wider">
                  Financial Forecast · 趋势 + 季节性 + 多情景
                </p>
              </div>
            </div>
            <button
              onClick={() => void loadForecastData()}
              disabled={loading}
              className="flex items-center gap-1 text-[10px] font-mono px-2 py-1 rounded border border-cyan-500/30 bg-cyan-500/10 text-cyan-300 hover:bg-cyan-500/20 transition disabled:opacity-50"
            >
              {loading ? <Loader2 size={10} className="animate-spin" /> : <RefreshCw size={10} />}
              刷新
            </button>
          </div>
          {/* 空状态 */}
          <div className="flex-1 flex flex-col items-center justify-center p-10">
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.4 }}
              className="w-16 h-16 rounded-full bg-white/[0.03] border border-white/[0.08] flex items-center justify-center mb-4"
            >
              <Database size={26} className="text-gray-600" />
            </motion.div>
            <p className="text-[13px] font-mono text-gray-400 mb-1">暂无历史数据 · 无法预测</p>
            <p className="text-[10px] font-mono text-gray-600 text-center leading-relaxed max-w-md">
              财务预测需要至少 2 个真实收入数据点。
              <br />
              收入数据来自 RevenueEngine（今日收入）与每日收入快照（由财务仪表盘自动记录）。
              <br />
              请先在「财务仪表盘」中刷新以积累每日收入历史，或在「财务三表」中录入成本/现金流数据。
            </p>
            {error && (
              <p className="text-[9px] font-mono text-red-400/70 mt-3">
                <AlertCircle size={10} className="inline mr-1" />
                {error}
              </p>
            )}
            <p className="text-[9px] font-mono text-gray-700 mt-4">
              白皮书铁律一：禁止假数据，无历史数据时不进行预测
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ============================================
  // 渲染：主视图
  // ============================================
  const metricOptions = Object.entries(METRIC_CONFIG) as Array<
    [ForecastMetric, (typeof METRIC_CONFIG)[ForecastMetric]]
  >;
  const scenarioOptions: Array<{ key: ForecastScenario | "all"; label: string; color: string }> = [
    { key: "optimistic", label: "乐观 +20%", color: SCENARIO_CONFIG.optimistic.color },
    { key: "neutral", label: "中性", color: SCENARIO_CONFIG.neutral.color },
    { key: "pessimistic", label: "悲观 -20%", color: SCENARIO_CONFIG.pessimistic.color },
    { key: "all", label: "全部对比", color: "#E5E7EB" },
  ];

  return (
    <div className="h-full w-full overflow-hidden p-4">
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="h-full flex flex-col rounded-xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-xl overflow-hidden"
      >
        {/* ============ Header ============ */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/[0.06]">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-md bg-cyan-500/15 border border-cyan-500/30 flex items-center justify-center">
              <BarChart3 size={16} className="text-cyan-400" />
            </div>
            <div>
              <h2 className="text-[15px] font-semibold text-white tracking-tight">财务预测</h2>
              <p className="text-[10px] font-mono text-gray-500 uppercase tracking-wider">
                Financial Forecast · 趋势 + 季节性 + 多情景
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {lastUpdated && (
              <span className="text-[9px] font-mono text-gray-600 hidden md:inline">
                {lastUpdated}
              </span>
            )}
            <button
              onClick={() => void loadForecastData()}
              disabled={loading}
              className="flex items-center gap-1 text-[10px] font-mono px-2 py-1 rounded border border-cyan-500/30 bg-cyan-500/10 text-cyan-300 hover:bg-cyan-500/20 transition disabled:opacity-50"
            >
              {loading ? <Loader2 size={10} className="animate-spin" /> : <RefreshCw size={10} />}
              {loading ? "加载中" : "刷新"}
            </button>
          </div>
        </div>

        {/* ============ 内容区 ============ */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {error && (
            <div className="px-4 py-2 rounded-md border border-red-500/20 bg-red-500/[0.03] text-[10px] font-mono text-red-400">
              <AlertCircle size={11} className="inline mr-1" />
              {error}
            </div>
          )}

          {/* ============ 控制栏：指标 + 情景 + 时间范围 ============ */}
          <div className="flex flex-wrap items-center gap-3">
            {/* 指标切换 */}
            <div className="flex items-center gap-1.5">
              <span className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mr-1">
                指标
              </span>
              <div className="flex items-center gap-0.5 p-0.5 rounded-md border border-white/[0.06] bg-white/[0.02]">
                {metricOptions.map(([key, cfg]) => {
                  const Icon = cfg.icon;
                  const active = metric === key;
                  return (
                    <button
                      key={key}
                      onClick={() => setMetric(key)}
                      className="relative flex items-center gap-1 px-2 py-1 rounded text-[10px] font-mono transition"
                      style={{
                        background: active ? `${cfg.color}20` : "transparent",
                        color: active ? cfg.color : "#6B7280",
                      }}
                    >
                      <Icon size={11} />
                      {cfg.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* 情景选择 */}
            <div className="flex items-center gap-1.5">
              <Sparkles size={11} className="text-gray-500" />
              <span className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mr-1">
                情景
              </span>
              <div className="flex items-center gap-0.5 p-0.5 rounded-md border border-white/[0.06] bg-white/[0.02]">
                {scenarioOptions.map((opt) => {
                  const active = scenario === opt.key;
                  return (
                    <button
                      key={opt.key}
                      onClick={() => setScenario(opt.key)}
                      className="px-2 py-1 rounded text-[10px] font-mono transition"
                      style={{
                        background: active ? `${opt.color}20` : "transparent",
                        color: active ? opt.color : "#6B7280",
                      }}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* 时间范围 */}
            <div className="flex items-center gap-1.5">
              <Clock size={11} className="text-gray-500" />
              <span className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mr-1">
                范围
              </span>
              <div className="flex items-center gap-0.5 p-0.5 rounded-md border border-white/[0.06] bg-white/[0.02]">
                {RANGE_OPTIONS.map((r) => {
                  const active = range === r;
                  return (
                    <button
                      key={r}
                      onClick={() => setRange(r)}
                      className="px-2 py-1 rounded text-[10px] font-mono transition"
                      style={{
                        background: active ? "rgba(34,211,238,0.2)" : "transparent",
                        color: active ? "#22D3EE" : "#6B7280",
                      }}
                    >
                      {r}天
                    </button>
                  );
                })}
              </div>
            </div>

            {/* 数据点统计 */}
            <div className="ml-auto flex items-center gap-1.5 text-[9px] font-mono text-gray-600">
              <Database size={10} />
              <span>历史样本 {revenueSeries.length} 天</span>
            </div>
          </div>

          {/* ============ 预测趋势图 ============ */}
          <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <metricCfg.icon size={12} style={{ color: metricCfg.color }} />
                <h3 className="text-[12px] font-medium text-white">
                  {metricCfg.label}预测趋势
                </h3>
                <span className="text-[9px] font-mono text-gray-500">
                  （历史实际 + {scenario === "all" ? "三情景" : SCENARIO_CONFIG[scenario as ForecastScenario].label}预测 + 置信区间 · 未来{range}天）
                </span>
              </div>
            </div>
            {loading && chartData.length === 0 ? (
              <div className="h-[300px] flex items-center justify-center text-gray-500">
                <Loader2 size={20} className="animate-spin" />
              </div>
            ) : (
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={chartData} margin={{ top: 8, right: 12, left: -8, bottom: 0 }}>
                    <defs>
                      <linearGradient id="forecastBandGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={metricCfg.color} stopOpacity={0.18} />
                        <stop offset="100%" stopColor={metricCfg.color} stopOpacity={0.04} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="#1e2a3f" strokeDasharray="3 3" vertical={false} />
                    <XAxis
                      dataKey="date"
                      tick={{ fill: "#9CA3AF", fontSize: 10, fontFamily: "monospace" }}
                      stroke="rgba(255,255,255,0.06)"
                      minTickGap={24}
                    />
                    <YAxis
                      tick={{ fill: "#9CA3AF", fontSize: 10, fontFamily: "monospace" }}
                      stroke="rgba(255,255,255,0.06)"
                      tickFormatter={(v: any) => {
                        const n = Number(v);
                        if (!Number.isFinite(n)) return "0";
                        return Math.abs(n) >= 10000
                          ? `${(n / 10000).toFixed(0)}万`
                          : String(Math.round(n));
                      }}
                    />
                    <Tooltip
                      contentStyle={{
                        background: "rgba(10,14,26,0.95)",
                        border: "1px solid rgba(34,211,238,0.3)",
                        borderRadius: "6px",
                        fontSize: "11px",
                        fontFamily: "monospace",
                      }}
                      labelStyle={{ color: "#E5E7EB" }}
                      formatter={(value: any, name: any) => {
                        if (name === "range") return ["置信区间", "区间"];
                        const labelMap: Record<string, string> = {
                          actual: "历史实际",
                          neutral: "中性预测",
                          optimistic: "乐观预测",
                          pessimistic: "悲观预测",
                        };
                        return [formatMoney(Number(value)), labelMap[String(name)] || String(name)];
                      }}
                    />
                    <Legend
                      wrapperStyle={{ fontSize: "9px", fontFamily: "monospace" }}
                      iconType="line"
                      formatter={(value: any) => {
                        const labelMap: Record<string, string> = {
                          range: "置信区间",
                          actual: "历史实际",
                          neutral: "中性预测",
                          optimistic: "乐观预测",
                          pessimistic: "悲观预测",
                        };
                        return labelMap[String(value)] || String(value);
                      }}
                    />
                    {/* 置信区间阴影（range 数组：[下限, 上限]） */}
                    <Area
                      dataKey="range"
                      stroke="none"
                      fill="url(#forecastBandGradient)"
                      isAnimationActive={false}
                      connectNulls
                    />
                    {/* 历史实际线 */}
                    <Line
                      dataKey="actual"
                      stroke="#F9FAFB"
                      strokeWidth={2}
                      dot={false}
                      connectNulls={false}
                      isAnimationActive={false}
                    />
                    {/* 三条情景预测线（按 scenario 控制显隐） */}
                    {(scenario === "all" || scenario === "optimistic") && (
                      <Line
                        dataKey="optimistic"
                        stroke={SCENARIO_CONFIG.optimistic.color}
                        strokeWidth={scenario === "optimistic" ? 1.8 : 1}
                        strokeDasharray="5 3"
                        dot={false}
                        connectNulls
                        isAnimationActive={false}
                      />
                    )}
                    {(scenario === "all" || scenario === "neutral") && (
                      <Line
                        dataKey="neutral"
                        stroke={SCENARIO_CONFIG.neutral.color}
                        strokeWidth={scenario === "neutral" ? 1.8 : 1.2}
                        strokeDasharray="5 3"
                        dot={false}
                        connectNulls
                        isAnimationActive={false}
                      />
                    )}
                    {(scenario === "all" || scenario === "pessimistic") && (
                      <Line
                        dataKey="pessimistic"
                        stroke={SCENARIO_CONFIG.pessimistic.color}
                        strokeWidth={scenario === "pessimistic" ? 1.8 : 1}
                        strokeDasharray="5 3"
                        dot={false}
                        connectNulls
                        isAnimationActive={false}
                      />
                    )}
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* ============ 预测摘要卡片 ============ */}
          <div>
            <div className="flex items-center gap-1.5 mb-2">
              <Calculator size={11} className="text-cyan-400" />
              <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">
                预测摘要（中性 · 未来{range}天）
              </span>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <SummaryCard
                label="预计总收入"
                value={formatMoney(summary.totalRevenue)}
                icon={TrendingUp}
                color="#10B981"
                hint={`未来${range}天`}
                delay={0}
              />
              <SummaryCard
                label="预计总支出"
                value={formatMoney(summary.totalExpense)}
                icon={TrendingDown}
                color="#EF4444"
                hint={`未来${range}天`}
                delay={0.05}
              />
              <SummaryCard
                label="净利润"
                value={formatMoney(summary.netProfit)}
                icon={Activity}
                color="#06B6D4"
                hint="收入-支出"
                delay={0.1}
              />
              <SummaryCard
                label="期末现金流"
                value={formatMoney(summary.endingCashflow)}
                icon={Wallet}
                color="#A855F7"
                hint="基线+预测"
                delay={0.15}
              />
            </div>
          </div>

          {/* ============ 预测准确度追踪 ============ */}
          <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <Target size={12} className="text-emerald-400" />
                <h3 className="text-[12px] font-medium text-white">预测准确度追踪</h3>
                <span className="text-[9px] font-mono text-gray-500">
                  （历史预测 vs 实际 · 收入指标）
                </span>
              </div>
              {accuracyStats.avg !== null ? (
                <div className="flex items-center gap-1.5">
                  <span className="text-[9px] font-mono text-gray-500">平均准确度</span>
                  <span
                    className="flex items-center gap-1 text-[12px] font-mono font-bold"
                    style={{
                      color:
                        accuracyStats.avg >= 0.85
                          ? "#10B981"
                          : accuracyStats.avg >= 0.6
                          ? "#F59E0B"
                          : "#EF4444",
                    }}
                  >
                    {accuracyStats.avg >= 0.85 ? (
                      <CheckCircle2 size={12} />
                    ) : accuracyStats.avg >= 0.6 ? (
                      <AlertCircle size={12} />
                    ) : (
                      <TrendingDown size={12} />
                    )}
                    {formatPercent(accuracyStats.avg)}
                  </span>
                  <span className="text-[9px] font-mono text-gray-600">
                    · {accuracyStats.count} 条已验证
                  </span>
                </div>
              ) : (
                <span className="text-[9px] font-mono text-gray-600">暂无已验证记录</span>
              )}
            </div>
            {accuracyStats.recent.length > 0 ? (
              <div className="overflow-hidden rounded-lg border border-white/[0.04]">
                {/* 表头 */}
                <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-white/[0.015] border-b border-white/[0.04] text-[9px] font-mono text-gray-500 uppercase tracking-wider">
                  <div className="col-span-3">目标日期</div>
                  <div className="col-span-3 text-right">预测值</div>
                  <div className="col-span-3 text-right">实际值</div>
                  <div className="col-span-3 text-right">准确度</div>
                </div>
                {/* 行 */}
                <AnimatePresence initial={false}>
                  {accuracyStats.recent.map((r, idx) => {
                    const acc = r.accuracy ?? 0;
                    const errRate = 1 - acc;
                    const accColor =
                      acc >= 0.85 ? "#10B981" : acc >= 0.6 ? "#F59E0B" : "#EF4444";
                    return (
                      <motion.div
                        key={r.id}
                        initial={{ opacity: 0, x: -6 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.2, delay: idx * 0.02 }}
                        className="grid grid-cols-12 gap-2 px-3 py-2 border-b border-white/[0.03] last:border-b-0 text-[11px] font-mono"
                      >
                        <div className="col-span-3 text-gray-300">{r.targetDate}</div>
                        <div className="col-span-3 text-right text-cyan-300">
                          {formatMoney(r.forecastValue)}
                        </div>
                        <div className="col-span-3 text-right text-gray-200">
                          {r.actualValue !== undefined ? formatMoney(r.actualValue) : "—"}
                        </div>
                        <div className="col-span-3 text-right flex items-center justify-end gap-1">
                          <span style={{ color: accColor }}>{formatPercent(acc)}</span>
                          <span className="text-[8px] text-gray-600">
                            (误差{formatPercent(errRate)})
                          </span>
                          {acc >= 0.85 ? (
                            <ArrowUpRight size={10} style={{ color: accColor }} />
                          ) : (
                            <ArrowDownRight size={10} style={{ color: accColor }} />
                          )}
                        </div>
                      </motion.div>
                    );
                  })}
                </AnimatePresence>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-6 text-center">
                <Target size={18} className="text-gray-700 mb-1.5" />
                <span className="text-[10px] font-mono text-gray-600 leading-relaxed">
                  暂无已验证的预测记录
                  <br />
                  系统会在每次刷新时记录未来预测，待目标日期到来并产生真实收入后自动回填准确度
                </span>
              </div>
            )}
          </div>

          {/* ============ 数据来源说明 ============ */}
          <div className="px-4 py-2 rounded-md border border-blue-500/15 bg-blue-500/[0.02] text-[10px] font-mono text-blue-300/70">
            <Database size={11} className="inline mr-1" />
            数据来源：RevenueEngine（today_revenue 真实收入）+ localStorage（xeaos_finance_statements 历史报表 +
            xeaos_finance_dashboard_targets 每日收入快照）。预测算法：移动平均 + 线性回归 + 季节性指数，不调用外部 AI。
            {lastUpdated && (
              <span className="ml-2 text-gray-500">· 最后更新：{lastUpdated}</span>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
}
